import React from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { OpenInNew as OpenInNewIcon, Replay as ReplayIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import type { OutcomeTemplate, OutcomeTierDef, OutcomeBranchAction, CijIntegrationConfig, Question, ResponseValue } from '../../types';
import { sanitizeMeaningfulHtml } from '../../utils/sanitizeHtml';
import { submitFormCapture } from '../../utils/formCapture';
import { verifyCijRecaptcha } from '../../utils/recaptcha';
import { getSafeOutcomeUrl as getSafeCtaUrl, getOutcomeExitTarget, navigateOutcomeExit } from '../../utils/outcomeNavigation';

interface OutcomeCardProps {
  tier: string;
  template: OutcomeTemplate;
  tierDef?: OutcomeTierDef;
  cijIntegration?: CijIntegrationConfig;
  responses?: Record<number, ResponseValue>;
  questions?: Question[];
  prospectEmail?: string | null;
}

function useOutcomeActions(
  cijIntegration: CijIntegrationConfig | undefined,
  responses: OutcomeCardProps['responses'],
  questions: Question[] | undefined,
  prospectEmail: string | null | undefined,
): {
  captureInFlight: boolean;
  processingStage: 'verification' | 'capture' | null;
  processingAction: string | null;
  captureError: string | null;
  protectedCta: boolean;
  handleCtaClick: (label: string, url?: string, buttonValue?: string, actionId?: string) => (e: React.MouseEvent) => void;
  handleBranch: (buttonValue: string, quizId: string, actionId: string) => void;
} {
  const navigate = useNavigate();
  const [captureInFlight, setCaptureInFlight] = React.useState(false);
  const [processingStage, setProcessingStage] = React.useState<'verification' | 'capture' | null>(null);
  const [processingAction, setProcessingAction] = React.useState<string | null>(null);
  const [captureError, setCaptureError] = React.useState<string | null>(null);
  const captureInFlightRef = React.useRef(false);
  const protectedCta = cijIntegration?.enabled === true && cijIntegration.recaptcha?.enabled === true;

  const submitCaptureThen = (label: string, actionId: string, afterSubmit: () => void): void => {
    if (!cijIntegration?.enabled) { afterSubmit(); return; }
    if (captureInFlightRef.current) return;
    captureInFlightRef.current = true;
    setCaptureInFlight(true);
    setProcessingAction(actionId);
    setProcessingStage(protectedCta ? 'verification' : 'capture');
    setCaptureError(null);
    const run = async (): Promise<void> => {
      try {
        if (protectedCta) {
          try {
            await verifyCijRecaptcha();
          } catch {
            setCaptureError('Verification failed. Please try again.');
            return;
          }
        }
        setProcessingStage('capture');
        try {
          await submitFormCapture(cijIntegration, responses, label, questions, prospectEmail);
        } catch (err) {
          console.error('Form capture submission failed', err);
        }
        afterSubmit();
      } finally {
        captureInFlightRef.current = false;
        setCaptureInFlight(false);
        setProcessingStage(null);
        setProcessingAction(null);
      }
    };
    void run();
  };

  const handleCtaClick = (label: string, url?: string, buttonValue = label, actionId = 'cta-primary') => (e: React.MouseEvent): void => {
    if (!cijIntegration?.enabled) return;
    e.preventDefault();
    submitCaptureThen(buttonValue, actionId, () => { navigateOutcomeExit(url); });
  };
  return {
    captureInFlight, processingStage, processingAction, captureError, protectedCta, handleCtaClick,
    handleBranch: (buttonValue, quizId, actionId) => submitCaptureThen(buttonValue, actionId, () => navigate(`/quiz/${quizId}`, { state: { prospectEmail } })),
  };
}

function OutcomeActionLabel({ label, actionId, processingAction, processingStage }: {
  label: string;
  actionId: string;
  processingAction: string | null;
  processingStage: 'verification' | 'capture' | null;
}): JSX.Element {
  const active = processingAction === actionId && processingStage !== null;
  if (!active) return <>{label}</>;
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
      <CircularProgress size={16} color="inherit" aria-hidden="true" />
      {processingStage === 'verification' ? 'Verifying…' : 'Submitting…'}
    </Box>
  );
}

function NegativeOutcomeCard({ tier, template, tierDef, cijIntegration, responses, prospectEmail, questions }: OutcomeCardProps): JSX.Element {
  const messageHtml = sanitizeMeaningfulHtml(template.message);
  const recommendationHtml = sanitizeMeaningfulHtml(template.recommendation);
  const headerDetailHtml = sanitizeMeaningfulHtml(tierDef?.headerDetail);
  const { captureInFlight, processingStage, processingAction, captureError, protectedCta, handleCtaClick, handleBranch } = useOutcomeActions(cijIntegration, responses, questions, prospectEmail);
  const ctaText = tierDef?.ctaText || template.cta;
  const ctaUrl = getSafeCtaUrl(tierDef?.ctaUrl || template.ctaUrl);
  const ctaText2 = tierDef?.ctaText2;
  const ctaUrl2 = getSafeCtaUrl(tierDef?.ctaUrl2);
  const hasPrimaryCta = Boolean(ctaText && ctaUrl);
  const hasSecondaryCta = Boolean(ctaText2 && ctaUrl2);
  const branchActions: OutcomeBranchAction[] = tierDef?.branchActions?.filter((action) => action.quizId && action.label.trim())
    ?? (tierDef?.nextQuizId ? [{ quizId: tierDef.nextQuizId, label: 'Continue to Next Quiz' }] : []);
  const hasActions = hasPrimaryCta || hasSecondaryCta || branchActions.length > 0;

  return (
    <Box className="cla-quiz-outcome cla-quiz-outcome-negative" data-quiz-outcome-tier={tier}>
      <Typography
        className="cla-quiz-outcome-header"
        variant="h4"
        sx={{ fontWeight: 700, color: '#1C3654', mb: headerDetailHtml ? 1 : 4, '& p': { m: 0 } }}
      >
        {tier}
      </Typography>
      {headerDetailHtml && (
        <Typography
          className="cla-quiz-outcome-header-detail"
          variant="body1"
          sx={{ color: '#1C3654', mb: 4, '& p': { m: 0 } }}
          dangerouslySetInnerHTML={{ __html: headerDetailHtml }}
        />
      )}
      {(messageHtml || recommendationHtml) && (
        <Box className="cla-quiz-outcome-content" sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 4 }}>
          {messageHtml && (
            <Box
              className="cla-quiz-outcome-message-box"
              sx={{
                flex: '1 1 260px',
                border: '1px solid',
                borderColor: 'divider',
                borderTop: '4px solid',
                borderTopColor: 'primary.main',
                borderRadius: 2,
                p: 3,
              }}
            >
              <Typography
                className="cla-quiz-outcome-message"
                variant="body1"
                sx={{ '& p:last-child': { m: 0 } }}
                dangerouslySetInnerHTML={{ __html: messageHtml }}
              />
            </Box>
          )}
          {recommendationHtml && (
            <Box
              className="cla-quiz-outcome-recommendation-box"
              sx={{
                flex: '1 1 260px',
                border: '1px solid',
                borderColor: 'divider',
                borderTop: '4px solid',
                borderTopColor: 'secondary.main',
                borderRadius: 2,
                p: 3,
              }}
            >
              <Box sx={{ border: '1px solid', borderColor: 'primary.main', borderRadius: 4, p: 2.5 }}>
                <Typography
                  className="cla-quiz-outcome-recommendation"
                  variant="body1"
                  sx={{ '& p': { m: 0 } }}
                  dangerouslySetInnerHTML={{ __html: recommendationHtml }}
                />
              </Box>
            </Box>
          )}
        </Box>
      )}
      {hasActions && (
        <Box sx={{ textAlign: 'center' }}>
          {captureError && <Alert severity="error">{captureError}</Alert>}
          {tierDef?.ctaHeader && (
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'secondary.main' }}>
              {tierDef.ctaHeader}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            {hasPrimaryCta && (
              <Button
                className="cla-quiz-button cla-quiz-button-cta"
                variant="contained"
                color="primary"
                {...(!protectedCta ? { href: ctaUrl!, target: getOutcomeExitTarget() } : {})}
                onClick={handleCtaClick(ctaText!, ctaUrl, tierDef?.ctaValue || ctaText!, 'cta-primary')}
                disabled={captureInFlight}
                aria-busy={processingAction === 'cta-primary' || undefined}
                aria-live={processingAction === 'cta-primary' ? 'polite' : undefined}
              >
                <OutcomeActionLabel label={ctaText!} actionId="cta-primary" processingAction={processingAction} processingStage={processingStage} />
              </Button>
            )}
            {hasSecondaryCta && (
              <Button
                className="cla-quiz-button cla-quiz-button-cta-2"
                variant="contained"
                color="primary"
                {...(!protectedCta ? { href: ctaUrl2!, target: getOutcomeExitTarget() } : {})}
                onClick={handleCtaClick(ctaText2!, ctaUrl2, tierDef?.ctaValue2 || ctaText2!, 'cta-secondary')}
                disabled={captureInFlight}
                aria-busy={processingAction === 'cta-secondary' || undefined}
                aria-live={processingAction === 'cta-secondary' ? 'polite' : undefined}
              >
                <OutcomeActionLabel label={ctaText2!} actionId="cta-secondary" processingAction={processingAction} processingStage={processingStage} />
              </Button>
            )}
            {branchActions.map((action, index) => (
              <Button
                className="cla-quiz-button cla-quiz-button-next-quiz"
                data-quiz-branch-index={index + 1}
                key={action.quizId}
                variant="contained"
                color="primary"
                onClick={() => handleBranch(action.cijValue || action.label, action.quizId, `branch-${action.quizId}`)}
                disabled={captureInFlight}
                aria-busy={processingAction === `branch-${action.quizId}` || undefined}
                aria-live={processingAction === `branch-${action.quizId}` ? 'polite' : undefined}
              >
                <OutcomeActionLabel label={action.label} actionId={`branch-${action.quizId}`} processingAction={processingAction} processingStage={processingStage} />
              </Button>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function PositiveOutcomeCard({ tier, template, tierDef, cijIntegration, responses, prospectEmail, questions }: OutcomeCardProps): JSX.Element {
  const messageHtml = sanitizeMeaningfulHtml(template.message);
  const recommendationHtml = sanitizeMeaningfulHtml(template.recommendation);
  const headerDetailHtml = sanitizeMeaningfulHtml(tierDef?.headerDetail);
  const { captureInFlight, processingStage, processingAction, captureError, protectedCta, handleCtaClick, handleBranch } = useOutcomeActions(cijIntegration, responses, questions, prospectEmail);
  const ctaText = tierDef?.ctaText || template.cta;
  const ctaUrl = getSafeCtaUrl(tierDef?.ctaUrl || template.ctaUrl);
  const ctaText2 = tierDef?.ctaText2;
  const ctaUrl2 = getSafeCtaUrl(tierDef?.ctaUrl2);
  const hasPrimaryCta = Boolean(ctaText && ctaUrl);
  const hasSecondaryCta = Boolean(ctaText2 && ctaUrl2);
  const branchActions: OutcomeBranchAction[] = tierDef?.branchActions?.filter((action) => action.quizId && action.label.trim())
    ?? (tierDef?.nextQuizId ? [{ quizId: tierDef.nextQuizId, label: 'Continue to Next Quiz' }] : []);
  const hasActions = hasPrimaryCta || hasSecondaryCta || branchActions.length > 0;

  return (
    <Box className="cla-quiz-outcome cla-quiz-outcome-positive" data-quiz-outcome-tier={tier}>
      <Typography variant="h4" sx={{ fontWeight: 700, color: '#1C3654', mb: 1 }}>
        {tier}
      </Typography>
      {headerDetailHtml && (
        <Typography
          className="cla-quiz-outcome-header-detail"
          variant="h6"
          sx={{ fontWeight: 500, color: '#1C3654', mb: 4, '& p': { m: 0 } }}
          dangerouslySetInnerHTML={{ __html: headerDetailHtml }}
        />
      )}
      {(messageHtml || recommendationHtml) && (
        <Box className="cla-quiz-outcome-content" sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 4 }}>
          {messageHtml && (
            <Box
              className="cla-quiz-outcome-message-box"
              sx={{
                flex: '1 1 260px',
                border: '1px solid',
                borderColor: 'divider',
                borderTop: '4px solid',
                borderTopColor: 'primary.main',
                borderRadius: 2,
                p: 3,
              }}
            >
              <Typography
                className="cla-quiz-outcome-message"
                variant="body1"
                sx={{ '& p:last-child': { m: 0 } }}
                dangerouslySetInnerHTML={{ __html: messageHtml }}
              />
            </Box>
          )}
          {recommendationHtml && (
            <Box
              className="cla-quiz-outcome-recommendation-box"
              sx={{
                flex: '1 1 260px',
                border: '1px solid',
                borderColor: 'divider',
                borderTop: '4px solid',
                borderTopColor: 'secondary.main',
                borderRadius: 2,
                p: 3,
              }}
            >
              <Typography
                className="cla-quiz-outcome-recommendation"
                variant="body1"
                sx={{ '& p:last-child': { m: 0 } }}
                dangerouslySetInnerHTML={{ __html: recommendationHtml }}
              />
            </Box>
          )}
        </Box>
      )}
      {hasActions && (
        <Box sx={{ textAlign: 'center' }}>
          {captureError && <Alert severity="error">{captureError}</Alert>}
          {tierDef?.ctaHeader && (
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'secondary.main' }}>
              {tierDef.ctaHeader}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            {branchActions.map((action, index) => (
              <Button
                className="cla-quiz-button cla-quiz-button-next-quiz"
                data-quiz-branch-index={index + 1}
                key={action.quizId}
                variant="contained"
                color="primary"
                onClick={() => handleBranch(action.cijValue || action.label, action.quizId, `branch-${action.quizId}`)}
                disabled={captureInFlight}
                aria-busy={processingAction === `branch-${action.quizId}` || undefined}
                aria-live={processingAction === `branch-${action.quizId}` ? 'polite' : undefined}
              >
                <OutcomeActionLabel label={action.label} actionId={`branch-${action.quizId}`} processingAction={processingAction} processingStage={processingStage} />
              </Button>
            ))}
            {hasPrimaryCta && (
              <Button
                className="cla-quiz-button cla-quiz-button-cta"
                variant="contained"
                color="primary"
                {...(!protectedCta ? { href: ctaUrl!, target: getOutcomeExitTarget() } : {})}
                onClick={handleCtaClick(ctaText!, ctaUrl, tierDef?.ctaValue || ctaText!, 'cta-primary')}
                disabled={captureInFlight}
                aria-busy={processingAction === 'cta-primary' || undefined}
                aria-live={processingAction === 'cta-primary' ? 'polite' : undefined}
              >
                <OutcomeActionLabel label={ctaText!} actionId="cta-primary" processingAction={processingAction} processingStage={processingStage} />
              </Button>
            )}
            {hasSecondaryCta && (
              <Button
                className="cla-quiz-button cla-quiz-button-cta-2"
                variant="contained"
                color="primary"
                {...(!protectedCta ? { href: ctaUrl2!, target: getOutcomeExitTarget() } : {})}
                onClick={handleCtaClick(ctaText2!, ctaUrl2, tierDef?.ctaValue2 || ctaText2!, 'cta-secondary')}
                disabled={captureInFlight}
                aria-busy={processingAction === 'cta-secondary' || undefined}
                aria-live={processingAction === 'cta-secondary' ? 'polite' : undefined}
              >
                <OutcomeActionLabel label={ctaText2!} actionId="cta-secondary" processingAction={processingAction} processingStage={processingStage} />
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function OutcomeCard({ tier, template, tierDef, cijIntegration, responses, prospectEmail, questions }: OutcomeCardProps): JSX.Element {
  const { captureInFlight, processingStage, processingAction, captureError, protectedCta, handleCtaClick, handleBranch } = useOutcomeActions(cijIntegration, responses, questions, prospectEmail);
  const ctaText = tierDef?.ctaText || template.cta;
  const ctaUrl = getSafeCtaUrl(tierDef?.ctaUrl || template.ctaUrl);
  const ctaText2 = tierDef?.ctaText2;
  const ctaUrl2 = getSafeCtaUrl(tierDef?.ctaUrl2);
  const hasPrimaryCta = Boolean(ctaText && ctaUrl);
  const hasSecondaryCta = Boolean(ctaText2 && ctaUrl2);
  const branchActions: OutcomeBranchAction[] = tierDef?.branchActions?.filter((action) => action.quizId && action.label.trim())
    ?? (tierDef?.nextQuizId ? [{ quizId: tierDef.nextQuizId, label: 'Continue to Next Quiz' }] : []);
  const hasActions = hasPrimaryCta || hasSecondaryCta || branchActions.length > 0;

  if (tierDef?.outcomeType === 'error') {
    return <NegativeOutcomeCard tier={tier} template={template} tierDef={tierDef} cijIntegration={cijIntegration} responses={responses} prospectEmail={prospectEmail} questions={questions} />;
  }

  if (tierDef?.outcomeType === 'success') {
    return <PositiveOutcomeCard tier={tier} template={template} tierDef={tierDef} cijIntegration={cijIntegration} responses={responses} prospectEmail={prospectEmail} questions={questions} />;
  }

  const messageHtml = sanitizeMeaningfulHtml(template.message);
  const recommendationHtml = sanitizeMeaningfulHtml(template.recommendation);
  const headerDetailHtml = sanitizeMeaningfulHtml(tierDef?.headerDetail);

  return (
    <Box className="cla-quiz-outcome" data-quiz-outcome-tier={tier} sx={{ mt: 1 }}>
      <Typography
        className="cla-quiz-outcome-header"
        variant="h4"
        sx={{ fontWeight: 700, color: '#1C3654', mb: headerDetailHtml ? 1 : 4, '& p': { m: 0 } }}
      >
        {tier}
      </Typography>
      {headerDetailHtml && (
        <Typography
          className="cla-quiz-outcome-header-detail"
          variant="body1"
          sx={{ color: '#1C3654', mb: 4, '& p': { m: 0 } }}
          dangerouslySetInnerHTML={{ __html: headerDetailHtml }}
        />
      )}
      {(messageHtml || recommendationHtml) && (
        <Box className="cla-quiz-outcome-content" sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 4 }}>
          {messageHtml && <Box className="cla-quiz-outcome-message-box" sx={{ flex: '1 1 260px', border: '1px solid', borderColor: 'divider', borderTop: '4px solid', borderTopColor: 'primary.main', borderRadius: 2, p: 3 }}>
            <Typography
              className="cla-quiz-outcome-message"
              variant="body1"
              sx={{ '& p:last-child': { m: 0 } }}
              dangerouslySetInnerHTML={{ __html: messageHtml }}
            />
          </Box>}
          {recommendationHtml && <Box className="cla-quiz-outcome-recommendation-box" sx={{ flex: '1 1 260px', border: '1px solid', borderColor: 'divider', borderTop: '4px solid', borderTopColor: 'secondary.main', borderRadius: 2, p: 3 }}>
            <Typography
              className="cla-quiz-outcome-recommendation"
              variant="body1"
              sx={{ '& p:last-child': { m: 0 } }}
              dangerouslySetInnerHTML={{ __html: recommendationHtml }}
            />
          </Box>}
        </Box>
      )}
      {hasActions && (
        <Box sx={{ textAlign: 'center' }}>
          {captureError && <Alert severity="error">{captureError}</Alert>}
          {tierDef?.ctaHeader && <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'secondary.main' }}>{tierDef.ctaHeader}</Typography>}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            {hasPrimaryCta && <Button className="cla-quiz-button cla-quiz-button-cta" variant="contained" {...(!protectedCta ? { href: ctaUrl!, target: getOutcomeExitTarget() } : {})} data-cij-value={tierDef?.ctaValue || ctaText} onClick={handleCtaClick(ctaText!, ctaUrl, tierDef?.ctaValue || ctaText, 'cta-primary')} endIcon={processingAction === 'cta-primary' ? undefined : <OpenInNewIcon />} disabled={captureInFlight} aria-busy={processingAction === 'cta-primary' || undefined} aria-live={processingAction === 'cta-primary' ? 'polite' : undefined}><OutcomeActionLabel label={ctaText!} actionId="cta-primary" processingAction={processingAction} processingStage={processingStage} /></Button>}
            {hasSecondaryCta && <Button className="cla-quiz-button cla-quiz-button-cta-2" variant="contained" {...(!protectedCta ? { href: ctaUrl2!, target: getOutcomeExitTarget() } : {})} data-cij-value={tierDef?.ctaValue2 || ctaText2} onClick={handleCtaClick(ctaText2!, ctaUrl2, tierDef?.ctaValue2 || ctaText2, 'cta-secondary')} endIcon={processingAction === 'cta-secondary' ? undefined : <OpenInNewIcon />} sx={{ ml: 1 }} disabled={captureInFlight} aria-busy={processingAction === 'cta-secondary' || undefined} aria-live={processingAction === 'cta-secondary' ? 'polite' : undefined}><OutcomeActionLabel label={ctaText2!} actionId="cta-secondary" processingAction={processingAction} processingStage={processingStage} /></Button>}
            {branchActions.map((action, index) => (
              <Button
                className="cla-quiz-button cla-quiz-button-next-quiz"
                data-quiz-branch-index={index + 1}
                key={action.quizId}
                variant={index === 0 ? 'contained' : 'outlined'}
                data-cij-value={action.cijValue || action.label}
                onClick={() => handleBranch(action.cijValue || action.label, action.quizId, `branch-${action.quizId}`)}
                endIcon={processingAction === `branch-${action.quizId}` ? undefined : <ReplayIcon />}
                disabled={captureInFlight}
                aria-busy={processingAction === `branch-${action.quizId}` || undefined}
                aria-live={processingAction === `branch-${action.quizId}` ? 'polite' : undefined}
                sx={{ ml: (hasPrimaryCta || index > 0) ? 1 : 0, mt: 1 }}
              >
                <OutcomeActionLabel label={action.label} actionId={`branch-${action.quizId}`} processingAction={processingAction} processingStage={processingStage} />
              </Button>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default OutcomeCard;
